import axios from 'axios'

// "Headers Timeout" is deliberately not retried: it is a slow failure, so a
// retry would double the wait on top of an already degraded backend.
const TRANSIENT_MESSAGE =
  /status code 5\d\d|other side closed|ECONNRESET|socket hang up/i

// The VTEX IO gateway sometimes answers 400 to a query that is valid: the same
// query/slug returns 200 on the next call (SITE-B2C-FASTSTORE-2ZC8). The body
// is checked so real 400s (bad input) are still not retried.
const isGraphqlValidationFailure = (data: unknown) =>
  (data as { errors?: Array<{ message?: string }> } | undefined)?.errors?.some(
    (e) => e?.message === 'GraphQL validation failed'
  ) === true

export const isTransientError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status

    return (
      status === undefined ||
      status >= 500 ||
      (status === 400 && isGraphqlValidationFailure(error.response?.data))
    )
  }

  return error instanceof Error && TRANSIENT_MESSAGE.test(error.message)
}

type RetryOptions = { retries?: number; delayMs?: number }

export const retryTransient = async <T>(
  fn: () => Promise<T>,
  { retries = 1, delayMs = 300 }: RetryOptions = {}
): Promise<T> => {
  try {
    return await fn()
  } catch (error) {
    if (retries <= 0 || !isTransientError(error)) throw error

    await new Promise((resolve) => setTimeout(resolve, delayMs))

    return retryTransient(fn, { retries: retries - 1, delayMs })
  }
}
